import { FloatingPortal, Placement, useId } from '@floating-ui/react';
import { CalendarSimpleOutlined } from '@icon';
import hoistNonReactStatics from 'hoist-non-react-statics';
import moment from 'moment';
import React from 'react';
import { useIntl } from 'react-intl';
import Form from '../../form';
import { TFormVariant } from '../../form/Form.types';
import { TextField } from '../../inputs';
import CalendarPanel from '../components/calendar-panel';
import { TDatePanelOption } from '../components/calendar-panel/calendar-panel.types';
import { TPanelMode, getDateFormat } from '../date-time.utils';
import DatePickerDays from './components/date-picker-days';
import DatePickerMonths from './components/date-picker-months';
import DatePickerYears from './components/date-picker-years';
import { useDatePickerCalendar, useFloatingPicker } from './date-picker.utils';
import classNames from 'classnames';

type DatePickerProps = {
  value?: Date[];
  onChange?: (dates: Date[]) => void;
  mode?: TPanelMode;
  locale?: Intl.LocalesArgument;
  minDate?: Date | undefined;
  maxDate?: Date | undefined;
  presets?: TDatePanelOption[];
  dateFormat?: string;
  disabled?: boolean;
  error?: boolean;
  placement?: Placement;
  clearable?: boolean;
  style?: React.CSSProperties;
  className?: string;
  variant?: TFormVariant;
  left?: React.ReactNode;
  right?: React.ReactNode;
  renderExtraFooter?: () => React.ReactNode;
  outlined?: boolean;
};

const DatePicker = (props: DatePickerProps, ref?: React.Ref<HTMLInputElement | null>) => {
  const {
    value,
    onChange,
    mode = 'date',
    locale,
    minDate,
    maxDate,
    presets,
    disabled,
    error,
    placement = 'bottom-start',
    style,
    className,
    clearable = true,
    variant,
    left,
    right,
    renderExtraFooter,
    outlined,
  } = props;
  const settings = Form.useFormSettings({ variant, disabled });
  const { locale: intlLocale } = useIntl();
  const mergedLocale = (locale ?? intlLocale) as string;
  const dateFormat = props.dateFormat ?? getDateFormat(mode, mergedLocale);

  const {
    floatingProps: { x, y, refs, strategy },
    interactionProps: { getFloatingProps, getReferenceProps },
    open,
    setOpen,
  } = useFloatingPicker({ placement });

  const headingId = useId();
  const referenceProps = getReferenceProps() as any;
  const [panelMode, setPanelMode] = React.useState<TPanelMode>('date');
  const [inputValue, setInputValue] = React.useState('');

  const { actions, data, propGetters } = useDatePickerCalendar({
    locale: mergedLocale,
    maxDate,
    minDate,
    onChange,
    value,
  });

  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleInputChange = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      const dateForm = moment(e.target.value, dateFormat, true);
      setInputValue(e.target.value);

      if (dateForm.isValid()) {
        const nextDate = dateForm.toDate();
        onChange?.([nextDate]);
      }
    },
    [dateFormat],
  );

  const resetInputValue = () => {
    const nextDate = value?.[0];

    if (nextDate) {
      setInputValue(moment(nextDate).format(dateFormat));
      actions.setMonth(nextDate);
      actions.setYear(nextDate);
    } else {
      setInputValue('');
    }
  };

  React.useImperativeHandle(ref, () => inputRef.current);

  React.useEffect(() => {
    resetInputValue();
  }, [value?.[0]?.toISOString(), dateFormat]);

  React.useEffect(() => {
    if (!open) {
      (refs.reference.current as HTMLInputElement)?.blur();
      setPanelMode(mode ?? 'date');
    }
  }, [open]);

  const currentMode = mode === 'date' || mode === 'month' ? panelMode : mode;

  return (
    <>
      <TextField
        outlined={outlined}
        className={classNames('ui-date-picker', className)}
        style={style}
        error={error}
        disabled={settings.disabled}
        clearable={clearable}
        onChange={handleInputChange}
        value={inputValue}
        placeholder={dateFormat}
        permanentPlaceholder
        left={left}
        right={
          right !== undefined ? (
            right
          ) : (
            <TextField.Action
              Icon={CalendarSimpleOutlined}
              onClick={() => {
                (refs.reference.current as HTMLInputElement)?.focus();
              }}
            />
          )
        }
        onClearClick={() => {
          onChange?.([]);
        }}
        onBlur={(e) => {
          resetInputValue();
          referenceProps.onBlur(e);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            inputRef.current?.blur();
          }
        }}
        onFocus={referenceProps.onFocus}
        ref={refs.setReference}
      />
      <FloatingPortal>
        {open ? (
          <CalendarPanel
            options={presets}
            onOptionClick={(_, sD) => {
              onChange?.(sD as [Date]);
              setOpen(false);
            }}
            ref={refs.setFloating}
            style={{
              position: strategy,
              transform: `translate(${x ?? 0}px, ${y ?? 0}px)`,
              top: 0,
              left: 0,
              zIndex: 9999999,
            }}
            aria-labelledby={headingId}
            {...getFloatingProps()}
            footer={renderExtraFooter?.()}
          >
            {currentMode === 'date' ? (
              <DatePickerDays
                mode={mode}
                setOpen={setOpen}
                setPanelMode={setPanelMode}
                calendars={data.calendars}
                propGetters={propGetters}
                weekDays={data.weekDays}
              />
            ) : null}
            {currentMode === 'month' ? (
              <DatePickerMonths
                setOpen={setOpen}
                setPanelMode={setPanelMode}
                onChange={onChange}
                mode={mode}
                calendars={data.calendars}
                months={data.months}
                propGetters={propGetters}
              />
            ) : null}
            {currentMode === 'year' ? (
              <DatePickerYears
                setOpen={setOpen}
                setPanelMode={setPanelMode}
                onChange={onChange}
                mode={mode}
                propGetters={propGetters}
                years={data.years}
              />
            ) : null}
          </CalendarPanel>
        ) : null}
      </FloatingPortal>
    </>
  );
};

DatePicker.Action = TextField.Action;
DatePicker.ClearAction = TextField.ClearAction;
DatePicker.Icon = TextField.Icon;

export default hoistNonReactStatics(React.forwardRef(DatePicker), DatePicker);
